import useGoogleSignIn from '@/hooks/useGoogleSignIn';
import useGoogleSignUp from '@/hooks/useGoogleSignUp';
import useLoadingOverlay from '@/hooks/useLoadingOverlay';
import { verifyInviteCodeQueryOptions } from '@/lib/query-options';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { GoogleButton } from '@/components/glitch-ui/google-button';
import { EmailSignupForm } from '@/components/auth/EmailSignupForm';
import logo from '/logo_lightbg.svg';

export const Route = createFileRoute('/invite-signup/$code/')({
  component: RegisterInvitedUser,
});

function RegisterInvitedUser() {
  const { code: inviteCode } = Route.useParams();
  const navigate = useNavigate();
  const [authMethod, setAuthMethod] = useState<'google' | 'email'>('google');
  const { signupWithGoogle, isLoading: isSignUpLoading } = useGoogleSignUp({
    redirectUrl: '/campaigns',
    inviteCode,
  });
  const { signInWithGoogle, isLoading: isSignInLoading } = useGoogleSignIn();
  const {
    data,
    isLoading,
    error,
  } = useQuery(verifyInviteCodeQueryOptions(inviteCode));
  
  // Redirect to signin if invite code is invalid (already used)
  useEffect(() => {
    if (error) {
      navigate({ to: '/signin' });
    }
  }, [error, navigate]);
  
  useLoadingOverlay(isSignUpLoading || isSignInLoading || isLoading);
  
  // Don't render anything if there's an error (redirecting)
  if (error) {
    return null;
  }
  
  // Don't render if still loading or no data yet
  if (isLoading || !data) {
    return null;
  }
  
  const { is_valid_refresh_token } = data;


  return (
    <div className='min-h-screen bg-gray-50 flex items-center justify-center p-4'>
      <div className='flex flex-col items-center'>
        <img src={logo} alt='logo' className='w-[117px] h-[27px] mb-8' />
        <Card className='w-[480px] h-fit'>
          <CardHeader className='lg:px-8 lg:pt-6'>
            <CardTitle className='text-gray-900 font-semibold text-[2rem] leading-9 text-center'>
              <p>Join Team</p>
            </CardTitle>
            <CardDescription className='text-gray-700 text-base py-4'>
              Glitch is your AI Growth Engine. Onboard in minutes, and our AI will help you get more leads for less.
            </CardDescription>
          </CardHeader>
          <CardContent className='lg:px-8 lg:pb-8 space-y-4'>
            {authMethod === 'google' ? (
              <GoogleButton 
                onClick={() => {
                  if (is_valid_refresh_token) signInWithGoogle();
                  else signupWithGoogle();
                }} 
              />
            ) : (
              <EmailSignupForm />
            )}
          </CardContent>
        </Card>
        <button 
          onClick={() => setAuthMethod(authMethod === 'google' ? 'email' : 'google')}
          className="mt-4 text-sm text-blue-600 hover:text-blue-800 underline"
        >
          {authMethod === 'google' ? 'use email' : 'use Google'}
        </button>
      </div>
    </div>
  );
}
